// web/src/App.tsx — YourProject.ai customer dashboard routes.
// /login, /invite/:token and /paused render standalone (no shell); everything else lives inside AppShell. Admin routes are gated by AdminGate
// (404-style for non-admins) AND hidden from nav in AppShell.
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { Home } from './screens/Home';
import { MySite } from './screens/MySite';
import { Build } from './screens/Build';
import { Dns } from './screens/Dns';
import { Stripe } from './screens/Stripe';
import { NewRequest } from './screens/NewRequest';
import { AddPhoto } from './screens/AddPhoto';
import { Requests } from './screens/Requests';
import { RequestDetail } from './screens/RequestDetail';
import { Billing } from './screens/Billing';
import { Support } from './screens/Support';
import { Settings } from './screens/Settings';
import { Reports } from './screens/Reports';
import { Login } from './screens/Login';
import { Invite } from './screens/Invite';
import { SitePaused } from './screens/SitePaused';
import { AdminGate } from './admin/AdminShell';
import { AdminOverview } from './admin/AdminOverview';
import { AdminCustomers } from './admin/AdminCustomers';
import { AdminCustomerDetail } from './admin/AdminCustomerDetail';
import { AdminUsers } from './admin/AdminUsers';
import { AdminBuilds } from './admin/AdminBuilds';
import { AdminBuildGate } from './admin/AdminBuildGate';
import { FEATURES } from './lib/features';
import { AdminTasks } from './admin/AdminTasks';
import { AdminHealth } from './admin/AdminHealth';
import { AdminSignals } from './admin/AdminSignals';
import { AdminLeads } from './admin/AdminLeads';
import { AdminBoard } from './admin/AdminBoard';
import { AdminClientDetail } from './admin/AdminClientDetail';
import { AdminLifecycle } from './admin/AdminLifecycle';
import { AdminOutbox } from './admin/AdminOutbox';
import { AdminReview } from './admin/AdminReview';
import { AdminRuns } from './admin/AdminRuns';

export default function App() {
  return (
    <Routes>
      {/* Standalone login (no shell) */}
      <Route path="/login" element={<Login />} />
      {/* Client invite link (portal spec 13): stash the token, sign in, AppShell accepts it */}
      <Route path="/invite/:token" element={<Invite />} />
      {/* Where a switched-off client site's visitors land (portal spec 25): public, no login */}
      <Route path="/paused" element={<SitePaused />} />

      {/* Everything else inside the app shell */}
      <Route path="/*" element={
        <AppShell>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/site" element={<MySite />} />
            <Route path="/dns" element={<Dns />} />
            {/* Hidden (portal spec 10): with VITE_BUILDS_UI_ENABLED off, /build falls to the * → / redirect. */}
            {FEATURES.builds && <Route path="/build" element={<Build />} />}
            <Route path="/stripe" element={<Stripe />} />
            <Route path="/request" element={<NewRequest />} />
            <Route path="/photo" element={<AddPhoto />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/requests/:id" element={<RequestDetail />} />
            {/* portal spec 17: the monthly reports (?site=<id> picks the site) */}
            <Route path="/reports" element={<Reports />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/support" element={<Support />} />
            <Route path="/settings" element={<Settings />} />

            {/* Admin — gated (portal spec 22). /admin is the pipeline board; the old overview is /admin/money. */}
            <Route path="/admin" element={<AdminGate><AdminBoard /></AdminGate>} />
            <Route path="/admin/clients/:slug" element={<AdminGate><AdminClientDetail /></AdminGate>} />
            <Route path="/admin/leads" element={<AdminGate><AdminLeads /></AdminGate>} />{/* spec 24 */}
            <Route path="/admin/lifecycle" element={<AdminGate><AdminLifecycle /></AdminGate>} />
            <Route path="/admin/outbox" element={<AdminGate><AdminOutbox /></AdminGate>} />
            <Route path="/admin/review" element={<AdminGate><AdminReview /></AdminGate>} />
            <Route path="/admin/runs" element={<AdminGate><AdminRuns /></AdminGate>} />
            <Route path="/admin/money" element={<AdminGate><AdminOverview /></AdminGate>} />
            <Route path="/admin/customers" element={<AdminGate><AdminCustomers /></AdminGate>} />
            <Route path="/admin/customers/:id" element={<AdminGate><AdminCustomerDetail /></AdminGate>} />
            <Route path="/admin/users" element={<AdminGate><AdminUsers /></AdminGate>} />
            {/* chalice-v2 doc 50 §3.4 — the needs_attention release valve; doc 51 §5 — the learnings-loop cluster view */}
            <Route path="/admin/health" element={<AdminGate><AdminHealth /></AdminGate>} />
            <Route path="/admin/signals" element={<AdminGate><AdminSignals /></AdminGate>} />
            {/* Hidden (portal spec 10): builds QA and chalice "Site updates" are routed only with their flags on; off, they
                fall to the * → / redirect and no nav item points at them (spec 22 §7 step 11). */}
            {FEATURES.builds && <Route path="/admin/builds" element={<AdminGate><AdminBuilds /></AdminGate>} />}
            {FEATURES.builds && <Route path="/admin/builds/:id" element={<AdminGate><AdminBuildGate /></AdminGate>} />}
            {FEATURES.chalice && <Route path="/admin/tasks" element={<AdminGate><AdminTasks /></AdminGate>} />}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppShell>
      } />
    </Routes>
  );
}
